import Konva from 'konva';
import axios, { AxiosResponse } from 'smg/axios';
import * as buttons from 'smg/buttons';
import { AddCartItems } from 'smg/customer-api/carts';
import * as models from 'smg/models';
import { RemToPx } from 'smg/rem';
import * as util from 'smg/util';
import { Relref } from '../hugo';
import * as stockitems from '../stockitems';
import { ColorLayer, WhiteLayer } from '../stockitems';

declare interface myWindow extends Window {
    HelenQuoteLoad: ($quoteParent: JQuery<HTMLElement>, success: () => void) => void;
}
declare const window: myWindow;

declare interface createUploadsResp {
    files: file[];
}

declare interface file {
    uploadID: number;
    url: string;
    name: string;
    mime: string;
}

declare interface point {
    x: number;
    y: number;
}

export default async function main() {
    stockitems.InitGallery();
    stockitems.InitSeeMore();
    void stockitems.InitPrices(true);
    const canvas = document.querySelector('#ornament-canvas') as HTMLDivElement;
    const fileInput = document.querySelector('#file-upload-ornament') as HTMLInputElement;
    const addToCartBtn = document.querySelector('#add-to-cart') as HTMLButtonElement;
    const quantity = document.querySelector('#quantity') as HTMLInputElement;
    const $editBtn = document.querySelector('#edit-btn') as HTMLButtonElement;
    const $editDoneBtn = document.querySelector('#edit-done-btn') as HTMLButtonElement;
    const $closeableUploadAlert = document.querySelector('#closeable-upload-alert') as HTMLElement;
    const $canvasTextBtnParent = document.querySelector('#canvas-text-button') as HTMLElement;
    const $canvasTextBtns: HTMLButtonElement[] = Array.from($canvasTextBtnParent.querySelectorAll('button'));
    const $imageSpinner = document.querySelector('#image-spinner') as HTMLElement;
    const $canvasParent = document.querySelector('#canvas-parent') as HTMLElement;
    const $changeImageBtn = document.querySelector('#change-image-btn') as HTMLButtonElement;
    const $galleryImgContainer = document.querySelector('#gallery-image-container') as HTMLElement;
    const $scrollUpBtn = document.querySelector('#scroll-up-btn') as HTMLElement;
    const $scrollDownBtn = document.querySelector('#scroll-down-btn') as HTMLElement;
    const $scrollLeftBtn = document.querySelector('#scroll-left-btn') as HTMLElement;
    const $scrollRightBtn = document.querySelector('#scroll-right-btn') as HTMLElement;

    const $stage = document.querySelector('#stage') as HTMLElement;

    const scrollBtns = [$scrollUpBtn, $scrollDownBtn, $scrollLeftBtn, $scrollRightBtn];

    for (const btn of scrollBtns) {
        btn.addEventListener('click', e => {
            e.preventDefault();

            switch (btn) {
            case $scrollUpBtn:
                scrollGallery({ top: -100 }, 'smooth');
                break;
            case $scrollDownBtn:
                scrollGallery({ top: 100 }, 'smooth');
                break;
            case $scrollLeftBtn:
                scrollGallery({ left: -100 }, 'smooth');
                break;
            case $scrollRightBtn:
                scrollGallery({ left: 100 }, 'smooth');
                break;
            }
        });
    }

    function scrollGallery(opts: ScrollToOptions, behavior?: ScrollBehavior) {
        $galleryImgContainer.scrollBy({
            top: opts.top,
            left: opts.left,
            behavior,
        });
    }

    fileInput.addEventListener('change', () => {
        $imageSpinner.classList.remove('d-none');
        $changeImageBtn.classList.add('d-none');
        $editDoneBtn.classList.add('d-none');
    });

    for (const $btn of $canvasTextBtns) {
        $btn.addEventListener('click', () => fileInput.click());
    }

    const $canvasContainer = document.querySelector('#ornament-canvas') as HTMLElement;

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    const ornamentImage = await new Promise<Konva.Image>(resolve => {
        img.onload = () =>
            resolve(new Konva.Image({
                image: img,
                draggable: false,
                listening: false,
                x: -img.naturalWidth / 2,
            }));
        img.src = `${Relref('/')}img/custom-photo-ornaments/ornamentBlank.png`;
    });

    const buttonsHeight =
        RemToPx(2.25) + // buttons height with padding
        2 + // buttons border
        RemToPx(2);

    const stage = new Konva.Stage({
        container: canvas,
    });

    function fitStageIntoParentContainer(firstRun = true) {
        const $canvasContainerChildren: ChildNode[] = [];
        if (!firstRun) {
            $canvasContainerChildren.push(...Array.from($canvasContainer.childNodes));
            $canvasContainer.innerHTML = '';
        }

        const ornamentMaxWidth = $stage.offsetWidth - // canvas container width
            RemToPx(2) * 2; // x padding

        const ornamentMaxHeight = $stage.offsetHeight - buttonsHeight; // buttons parent padding

        const widthRatio = ornamentMaxWidth / img.naturalWidth,
            heightRatio = ornamentMaxHeight / img.naturalHeight;
        const bestRatio = Math.min(widthRatio, heightRatio);

        const offsetWidth = $stage.offsetWidth;
        const offsetHeight = $stage.offsetHeight;

        if ($canvasContainerChildren) {
            for (const c of $canvasContainerChildren) {
                $canvasContainer.appendChild(c);
            }
        }

        $canvasTextBtnParent.setAttribute('style', `--bestRatio:${bestRatio}`);
        $imageSpinner.style.top = `${520 * bestRatio}px`;

        stage.width(offsetWidth);
        stage.height(offsetHeight);
        stage.offsetX((-offsetWidth / 2) * (1 / bestRatio));
        stage.scale({ x: bestRatio, y: bestRatio });
    }

    fitStageIntoParentContainer();
    $canvasTextBtnParent.classList.remove('d-none');

    window.addEventListener('resize', () => fitStageIntoParentContainer(false));

    const addClipFunc = (l: Konva.Layer) => {
        l.clipFunc(ctx => {
            ctx.arc(-2, 569, 260, 0, Math.PI * 2, false);
        });
    };

    const removeClipFunc = (l: Konva.Layer) => {
        l.clipFunc(ctx => {
            ctx.rect(-stage.width() / 2 / stage.scaleX(), 0, stage.width() / stage.scaleX(), stage.height() / stage.scaleY());
        });
    };

    const ornamentLayer = new Konva.Layer({
        id: 'ornament-layer',
        listening: false,
    });
    stage.add(ornamentLayer);

    ornamentLayer.add(ornamentImage);

    const customImageLayer = new Konva.Layer({ id: 'custom-image' });
    addClipFunc(customImageLayer);

    stage.add(customImageLayer);

    let image: Konva.Image;

    const addImage = (src: string) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = src;

        if (!image) {
            image = new Konva.Image({
                image: img,
                draggable: true,
                visible: false,
            });
            customImageLayer.add(image);
        } else {
            image.image(img);
        }

        img.onload = () => {
            $imageSpinner.classList.add('d-none');
            const diameter = 520;

            const aspectRatio = img.width / img.height;

            if (aspectRatio < 1) {
                image.width(diameter);
                image.height(diameter * img.height / img.width);
            } else {
                image.height(diameter);
                image.width(diameter * img.width / img.height);
            }

            image.x(-image.width() / 2 - 2);
            image.y((stage.height() / stage.scaleY() - 50) / 2 - image.height() / 2);
            image.visible(true);
        };

        image.on('click tap dragstart', () => adjustPicture());
    };

    $canvasParent.addEventListener('dragover', e => e.preventDefault());
    const onDrop = (e: DragEvent) => {
        e.preventDefault();
        $canvasTextBtnParent.classList.add('d-none');
        $editBtn.classList.remove('d-none');
        $editBtn.parentElement?.classList.remove('pb-lg-5');

        if (e.dataTransfer?.files?.length) {
            const f = e.dataTransfer?.files[0];
            customImageEvent(f);
        }
    };
    $canvasParent.addEventListener('drop', onDrop);

    const pictureReady = () => {
        transformer.nodes([]);
        ornamentImage.opacity(1);
        addClipFunc(customImageLayer);
    };

    const adjustPicture = () => {
        transformer.nodes([image]);
        ornamentImage.opacity(.33);
        removeClipFunc(customImageLayer);
        $editBtn.classList.add('d-none');
        $editDoneBtn.classList.remove('d-none');
        $changeImageBtn.classList.remove('d-none');
        $changeImageBtn.addEventListener('click', e => {
            e.preventDefault();
            fileInput.click();
        });
        $editDoneBtn.addEventListener('click', e => {
            e.preventDefault();
            $editBtn.classList.remove('d-none');
            $editDoneBtn.classList.add('d-none');
            $changeImageBtn.classList.add('d-none');
            pictureReady();
        });
    };

    const transformer = new Konva.Transformer({
        resizeEnabled: true,
        rotateEnabled: true,
        visible: true,
    });
    customImageLayer.add(transformer);

    const hasImage = () => {
        for (const c of customImageLayer.getChildren()) {
            if (c.className.toLowerCase() === 'image') {
                return true;
            }
        }
        return false;
    };

    const isFocused = () => transformer.nodes().length;
    const increaseImageScale = (s: number) => {
        const scale = image?.scale();
        if (!scale) {
            return;
        }
        image.scale({
            x: scale.x + s,
            y: scale.y + s,
        });
    };
    const decreaseImageScale = (s: number) => {
        const scale = image?.scale();
        if (!scale) {
            return;
        }
        image.scale({
            x: scale.x - s,
            y: scale.y - s,
        });
    };

    const scale = 0.025;
    const adjustImageZoom = (pos: point, zoomIn: boolean) => {
        if (zoomIn) {
            increaseImageScale(scale);
        } else {
            decreaseImageScale(scale);
        }

        const imagePos = image.getPosition();
        const imgWidth = image.width() * image.scaleX();
        const imgHeight = image.height() * image.scaleY();

        const inXBoundary = !(imagePos.x >= pos.x || imagePos.x + imgWidth <= pos.x);
        const inYBoundary = !(imagePos.y >= pos.y || imagePos.y + imgHeight <= pos.y);

        if (inXBoundary && inYBoundary) {
            image.setPosition({
                x: imagePos.x + (pos.x - imagePos.x) * scale * (zoomIn ? -1 : 1),
                y: imagePos.y + (pos.y - imagePos.y) * scale * (zoomIn ? -1 : 1),
            });
        }
    };

    stage.on('wheel', e => {
        if (!isFocused()) {
            return;
        }
        e.evt.preventDefault();

        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
            adjustImageZoom({ x: pointerPos.x, y: pointerPos.y }, e.evt.deltaY < 0);
        }
    });

    const center = (p1: point, p2: point): point => ({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
    const distance = (p1: point, p2: point): number => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

    let lastCenter: point | null = null;
    let lastDist: number | null = 0;
    stage.on('touchmove', e => {
        if (!isFocused()) {
            return;
        }
        e.evt.preventDefault();

        const touchOne = e.evt.touches[0];
        const touchTwo = e.evt.touches[1];

        if (touchOne && touchTwo) {
            image.stopDrag();
            transformer.stopDrag();

            const posOne: point = { x: touchOne.clientX, y: touchOne.clientY };
            const posTwo: point = { x: touchTwo.clientX, y: touchTwo.clientY };
            const cent = center(posOne, posTwo);

            if (!lastCenter) {
                lastCenter = cent;
                return;
            }
            const newCenter = cent;
            const dist = distance(posOne, posTwo);

            if (!lastDist) {
                lastDist = dist;
            }

            if (dist === lastDist) {
                return;
            }

            const imagePos = image.getPosition();
            const zoomIn = Math.sign(dist - lastDist) > 0;
            const touchScale = scale / 2;

            if (zoomIn) {
                increaseImageScale(scale);
            } else {
                decreaseImageScale(scale);
            }

            image.setPosition({
                x: imagePos.x + (newCenter.x - imagePos.x) * touchScale * (zoomIn ? -1 : 1),
                y: imagePos.y + (newCenter.y - imagePos.y) * touchScale * (zoomIn ? -1 : 1),
            });

            lastDist = dist;
        }
    });

    stage.on('touchend', () => {
        lastDist = 0;
        lastCenter = null;
    });

    let uploadID: number;
    const customImageEvent = (f: File) => {
        $imageSpinner.classList.remove('d-none');
        const file: { name: string; mime: string } = {
            name: f.name,
            mime: f.type,
        };

        void axios.post<createUploadsResp>('/uploads', {
            'files': [file],
            'region': 'us',
        }).then(resp => {
            uploadID = resp.data.files[0].uploadID;
            void axios.put(resp.data.files[0].url, f, {
                headers: {
                    'Content-Type': 'binary/octet-stream',
                },
                withCredentials: false,
            }).then(() => {
                const url = URL.createObjectURL(f);
                addImage(url);
            });
        });
    };

    $editBtn.addEventListener('click', e => {
        e.preventDefault();
        if (!hasImage()) {
            return;
        }

        if (!isFocused()) {
            adjustPicture();
        } else {
            $editBtn.classList.remove('d-none');
            $editDoneBtn.classList.add('d-none');
            $changeImageBtn.classList.add('d-none');
            pictureReady();
        }
    });

    const container = stage.container();
    container.addEventListener('drop', onDrop);

    fileInput.addEventListener('change', () => {
        $canvasTextBtnParent.classList.add('d-none');
        $editBtn.classList.remove('d-none');
        $closeableUploadAlert.classList.add('d-none');
        if (!fileInput.files?.length) {
            return;
        }

        customImageEvent(fileInput.files[0]);
    });

    quantity.addEventListener('input', e => {
        e.preventDefault();
    });

    document.addEventListener('click', e => {
        if (e.target instanceof HTMLCanvasElement || e.target === $editBtn) {
            return;
        }
        pictureReady();
    });

    addToCartBtn.addEventListener('click', async e => {
        e.preventDefault();
        buttons.Start(addToCartBtn);
        const quant = quantity.valueAsNumber;
        if (isNaN(quant) || quant <= 0) {
            buttons.Stop(addToCartBtn);
            return;
        }

        if (!uploadID) {
            $closeableUploadAlert.classList.remove('d-none');
            buttons.Stop(addToCartBtn);
            return;
        }

        pictureReady();

        ornamentImage.hide();

        const scale = stage.scaleX();
        const frontURL = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: (stage.width() / 2) - ((520 * scale) / 2) - (2 * scale),
            y: 309 * scale,
            width: 520 * scale,
            height: 520 * scale,
            pixelRatio: (1200 * 1.535) / (520 * scale), // pixels per inch, div by the ornament upload width
        });

        ornamentImage.show();

        const files: { name: string; mime: string }[] = [{
            name: '.front.jpg',
            mime: 'image/jpeg',
        }, {
            name: '.front-white.jpg',
            mime: 'image/jpeg',
        }];
        const images: Blob[] = [util.DataURItoBlob(await ColorLayer(frontURL), 'image/jpeg'), util.DataURItoBlob(await WhiteLayer(frontURL), 'image/jpeg')];
        const ids: number[] = [];

        const putPromises: Promise<AxiosResponse>[] = [];

        await axios.post<createUploadsResp>('/uploads', {
            files,
        }).then(r => {
            for (let i = 0; i < r.data.files.length; i++) {
                ids.push(r.data.files[i].uploadID);
                putPromises.push(axios.put(r.data.files[i].url, images[i], {
                    headers: {
                        'Content-Type': 'binary/octet-stream',
                    },
                    withCredentials: false,
                }));
            }
        });

        await Promise.all(putPromises);

        ids.push(uploadID);

        await AddCartItems({
            stockItemID: models.StockItemID.STOCK_ITEM_CHRISTMAS_ORNAMENT,
            quantity: quant,
            cartItemUploadIDs: ids,
        });

        window.location.href = Relref('/cart');
    });
}
